/**
 * NOT-590 — where a dragged category folder actually lands.
 *
 * A flat sortable list can only express "put this row here". A tree also needs
 * "and at THIS depth", which is taken from how far the row was dragged
 * horizontally — the standard dnd-kit sortable-tree projection. Without it a
 * drag can only ever re-sibling a folder, so nesting becomes impossible by the
 * one gesture a tree is expected to support.
 *
 * Pure and separately tested: this decides whether a drag can corrupt the tree,
 * and that is not something to leave to a `useCallback` nobody can exercise.
 */
import type { CategoryPosition, PricebookCategoryNode } from '@/types/pricebook';

/** Pixels of indent per tree level — also the drag distance that re-nests. */
export const INDENT_PX = 16;

export interface DragRow {
  id: string;
  depth: number;
  parentId: string | null;
}

export interface DropProjection {
  /** Full renumbered arrangement, ready to persist in one call. */
  positions: CategoryPosition[];
  /** Where the dragged row ended up. */
  parentId: string | null;
  depth: number;
}

/**
 * Project a drag onto a concrete arrangement, or `null` when the drop is a
 * no-op or would be illegal.
 *
 * `descendantIds` are the ids beneath the dragged row; dropping a folder into
 * its own subtree would detach it from the root, so it is refused rather than
 * silently corrected.
 */
export function projectDrop(args: {
  rows: DragRow[];
  activeId: string;
  overId: string;
  deltaX: number;
  descendantIds: Set<string>;
}): DropProjection | null {
  const { rows, activeId, overId, deltaX, descendantIds } = args;

  const from = rows.findIndex((row) => row.id === activeId);
  const to = rows.findIndex((row) => row.id === overId);
  if (from < 0 || to < 0) return null;

  const dragged = rows[from]!;
  const moved = [...rows];
  moved.splice(to, 0, ...moved.splice(from, 1));
  const movedIndex = moved.findIndex((row) => row.id === activeId);

  const requestedDepth = dragged.depth + Math.round(deltaX / INDENT_PX);

  // A row can nest at most one level below the row above it. Anything deeper
  // would skip a level and leave the tree with no parent to attach to.
  const above = moved[movedIndex - 1];
  const maxDepth = above && !descendantIds.has(above.id) ? above.depth + 1 : 0;
  const depth = Math.max(0, Math.min(requestedDepth, maxDepth));

  // The new parent is the nearest row above sitting exactly one level
  // shallower than where the dragged row now sits.
  let parentId: string | null = null;
  if (depth > 0) {
    for (let i = movedIndex - 1; i >= 0; i -= 1) {
      const candidate = moved[i]!;
      const candidateDepth = candidate.id === activeId ? depth : candidate.depth;
      if (candidateDepth === depth - 1) {
        parentId = candidate.id;
        break;
      }
    }
  }

  if (parentId && descendantIds.has(parentId)) return null;
  // Nothing actually moved.
  if (from === to && parentId === dragged.parentId) return null;

  // Renumber every sibling group so the persisted order matches what the user
  // just saw, rather than leaving gaps that drift on the next drag.
  const orderByParent = new Map<string | null, number>();
  const positions: CategoryPosition[] = moved.map((row) => {
    const rowParent = row.id === activeId ? parentId : row.parentId;
    const next = orderByParent.get(rowParent) ?? 0;
    orderByParent.set(rowParent, next + 1);
    return { id: row.id, parentId: rowParent, displayOrder: next };
  });

  return { positions, parentId, depth };
}

/**
 * Re-shape a tree by the positions a drop produced, WITHOUT waiting for the
 * server.
 *
 * The reorder was persist-then-refetch, so a dropped folder sprang back to
 * where it started and only jumped to its new home two round-trips later. That
 * reads as a glitch rather than as latency — the gesture appears to have failed
 * and then undone itself. Applying the same positions to the cached tree makes
 * the drop land immediately; the refetch afterwards only confirms it.
 *
 * A parent chain that would close a cycle is dropped rather than followed, so a
 * bad payload cannot hang the render.
 */
export function applyPositions(
  nodes: PricebookCategoryNode[],
  positions: CategoryPosition[],
): PricebookCategoryNode[] {
  if (positions.length === 0) return nodes;

  const overrides = new Map(positions.map((p) => [p.id, p]));

  interface Entry {
    node: PricebookCategoryNode;
    parentId: string | null;
    displayOrder: number;
  }

  const flat: Entry[] = [];
  const collect = (list: PricebookCategoryNode[], parentId: string | null): void => {
    for (const node of list) {
      const override = overrides.get(node.id);
      flat.push({
        node,
        // `parentId` is optional on a position: absent means "same parent, new
        // order", which is the common sibling-reorder case.
        parentId: override && override.parentId !== undefined ? override.parentId : parentId,
        displayOrder: override ? override.displayOrder : node.displayOrder,
      });
      collect(node.children, node.id);
    }
  };
  collect(nodes, null);

  const knownIds = new Set(flat.map((entry) => entry.node.id));

  const byParent = new Map<string | null, Entry[]>();
  for (const entry of flat) {
    /**
     * Mirror the server: a node whose parent is not in THIS tree surfaces at
     * the root rather than vanishing.
     *
     * Cached trees legitimately differ. The browse tree re-roots the visible
     * children of a folder that is hidden from the viewer (see `tree()` in
     * pricebook-categories.service), so one cache can hold a child while
     * another holds both. Since every cached tree is patched, a position can
     * name a parent that this particular cache has never heard of — and
     * bucketing under an id nothing reaches from the root would make the
     * folder disappear until the refetch landed.
     *
     * The node keeps its real `parentId`, exactly as the server leaves it; only
     * where it hangs in this view changes.
     */
    const bucket = entry.parentId !== null && knownIds.has(entry.parentId) ? entry.parentId : null;
    const siblings = byParent.get(bucket);
    if (siblings) siblings.push(entry);
    else byParent.set(bucket, [entry]);
  }

  const build = (parentId: string | null, seen: Set<string>): PricebookCategoryNode[] =>
    (byParent.get(parentId) ?? [])
      .filter((entry) => !seen.has(entry.node.id))
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((entry) => ({
        ...entry.node,
        parentId: entry.parentId,
        displayOrder: entry.displayOrder,
        children: build(entry.node.id, new Set(seen).add(entry.node.id)),
      }));

  return build(null, new Set());
}
